/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { Button, Card, Col, Form, message, Row, Space } from 'antd';
import { Formik } from 'formik';
import { omit } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type FiservPaymentModuleSpecsFragment } from '../../../../api/fragments/FiservPaymentModuleSpecs';
import { type FiservPaymentSettingsSpecFragment } from '../../../../api/fragments/FiservPaymentSettingsSpec';
import {
    UpdateFiservPaymentSettingsDocument,
    type UpdateFiservPaymentSettingsMutation,
    type UpdateFiservPaymentSettingsMutationVariables,
} from '../../../../api/mutations/updateFiservPaymentSettings';
import { type FiservPaymentUpdateSettingsInput } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import InputNumberField from '../../../../components/fields/InputNumberField';
import PasswordField from '../../../../components/fields/PasswordField';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validator from './validators';

export type FormValues = FiservPaymentUpdateSettingsInput;

export type UpdateFiservPaymentSettingInnerPageProps = {
    module: FiservPaymentModuleSpecsFragment;
    paymentSettings: FiservPaymentSettingsSpecFragment & {
        secretKey?: string;
    };
};

const colSpan = { lg: 8, md: 12, xs: 24 };

const UpdateFiservPaymentSettingInnerPage = ({ module, paymentSettings }: UpdateFiservPaymentSettingInnerPageProps) => {
    const moduleId = module.id;
    const paymentSettingsId = paymentSettings.id;

    const { t } = useTranslation(['fiservPaymentModuleDetails']);
    const apolloClient = useApolloClient();
    const navigate = useNavigate();

    const initialValues = useMemo(
        (): FormValues => ({
            ...omit(['id', 'paymentModuleId'], paymentSettings),
        }),
        [paymentSettings]
    );

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('fiservPaymentModuleDetails:paymentSettingsDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateFiservPaymentSettingsMutation, UpdateFiservPaymentSettingsMutationVariables>({
                    mutation: UpdateFiservPaymentSettingsDocument,
                    variables: { moduleId, id: paymentSettingsId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('fiservPaymentModuleDetails:paymentSettingsDrawer.messages.updateSuccess'),
            });

            navigate(`/admin/system/modules/${moduleId}`);
        },
        [t, apolloClient, moduleId, paymentSettingsId, navigate]
    );

    const validate = useValidator(validator(false));

    return (
        <ConsolePageWithHeader
            footer={[
                <Button key="submit" form="paymentSettingsForm" htmlType="submit" type="primary">
                    {t('fiservPaymentModuleDetails:paymentSettingsDrawer.updateButton')}
                </Button>,
            ]}
            onBack={() => navigate(`/admin/system/modules/${moduleId}`)}
            title={t('fiservPaymentModuleDetails:paymentSettingsDrawer.title.update')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form
                        id="paymentSettingsForm"
                        layout="vertical"
                        name="paymentSettingsForm"
                        onSubmitCapture={handleSubmit}
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Card title={t('fiservPaymentModuleDetails:dividers.mainDetails')}>
                                <Row gutter={14}>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t(
                                                'fiservPaymentModuleDetails:paymentSettingsDrawer.fields.displayName',
                                                {
                                                    returnObjects: true,
                                                }
                                            )}
                                            name="displayName"
                                            required
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t('fiservPaymentModuleDetails:paymentSettingsDrawer.fields.currency', {
                                                returnObjects: true,
                                            })}
                                            name="currency"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputNumberField
                                            {...t('fiservPaymentModuleDetails:paymentSettingsDrawer.fields.amount', {
                                                returnObjects: true,
                                            })}
                                            name="amount"
                                        />
                                    </Col>
                                </Row>
                            </Card>
                            <Card title={t('fiservPaymentModuleDetails:dividers.paymentSettings')}>
                                <InputField
                                    {...t('fiservPaymentModuleDetails:paymentSettingsDrawer.fields.storeName', {
                                        returnObjects: true,
                                    })}
                                    name="storeName"
                                />
                                <PasswordField
                                    {...t('fiservPaymentModuleDetails:paymentSettingsDrawer.fields.secretKey', {
                                        returnObjects: true,
                                    })}
                                    name="secretKey"
                                />
                                <InputField
                                    {...t('fiservPaymentModuleDetails:paymentSettingsDrawer.fields.paymentEndpoint', {
                                        returnObjects: true,
                                    })}
                                    name="paymentEndpoint"
                                />
                            </Card>
                        </Space>
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default UpdateFiservPaymentSettingInnerPage;
